import React, { useMemo } from "react";

/**
 * Splits a string into per-word, per-character spans so the IntersectionObserver
 * in `useReveal.js` can stagger their entrance into view. Words are kept
 * together (white-space: nowrap on each word wrapper) so the browser only
 * breaks lines on natural word boundaries — never mid-word.
 *
 *   <AnimatedText text="Beautiful sunsets" stagger={28} />
 */
const AnimatedText = ({
  text,
  stagger = 28,            // ms between each character
  baseDelay = 0,           // initial delay in ms before first char
  className = "",
  as: Tag = "span",
  italic = false,
}) => {
  const words = useMemo(() => {
    const raw = String(text);
    // Preserve spaces as actual gaps between word wrappers (single space rendered via margin).
    const parts = raw.split(/(\s+)/); // keeps whitespace tokens
    let charIndex = 0;
    return parts.map((part, wi) => {
      if (/^\s+$/.test(part)) {
        return { type: "space", key: `s-${wi}`, value: part };
      }
      const chars = Array.from(part).map((ch) => {
        const delay = baseDelay + charIndex * stagger;
        charIndex += 1;
        return { ch, delay };
      });
      return { type: "word", key: `w-${wi}`, chars };
    });
  }, [text, stagger, baseDelay]);

  return (
    <Tag
      className={className}
      aria-label={text}
      style={italic ? { fontStyle: "italic" } : undefined}
    >
      {words.map((w) =>
        w.type === "space" ? (
          <span key={w.key} aria-hidden="true">{"\u00A0"}</span>
        ) : (
          <span
            key={w.key}
            className="inline-block align-baseline"
            style={{ whiteSpace: "nowrap" }}
          >
            {w.chars.map(({ ch, delay }, ci) => (
              <span
                key={`${w.key}-${ci}`}
                className="char"
                style={{ transitionDelay: `${delay}ms` }}
                aria-hidden="true"
              >
                {ch}
              </span>
            ))}
          </span>
        )
      )}
    </Tag>
  );
};

export default AnimatedText;
